import type { StatutTache } from '@admin-pmvb/core'

// Confettis colorés quand une tâche se termine : deux canons en bas de l'écran, sur un canvas
// posé au-dessus de la page, sans clic ni lecteur d'écran. Rien si la personne a demandé moins
// d'animations. Calcul des particules pur, testé à part ; le dessin seul touche au DOM.

export const COULEURS_CONFETTIS = [
  '#ff3b5c',
  '#ffb800',
  '#2ec4b6',
  '#3a86ff',
  '#8338ec',
  '#ff6b00',
  '#06d6a0',
  '#ff4fd8',
] as const

/** Pesanteur et freinage de l'air, par image de 16,7 ms. */
const PESANTEUR = 0.32
const FREINAGE = 0.988
/** Durée de vie d'un confetti ; il s'efface pendant la dernière seconde. */
const VIE_MS = 4200
const EFFACEMENT_MS = 1000
/** Par canon. */
const NOMBRE = 90

export type Confetti = {
  x: number
  y: number
  vx: number
  vy: number
  angle: number
  vitesseAngle: number
  /** Phase du retournement (le confetti tourne sur lui-même et papillonne). */
  bascule: number
  vitesseBascule: number
  largeur: number
  hauteur: number
  couleur: string
  rond: boolean
  ageMs: number
}

const OUVERTS: readonly StatutTache[] = ['a_faire', 'en_cours', 'en_attente_tiers']

/** La tâche vient-elle de passer d'un statut ouvert à « faite » ? (pas au chargement) */
export function vientDEtreFaite(
  avant: StatutTache | undefined,
  apres: StatutTache | undefined,
): boolean {
  return avant !== undefined && OUVERTS.includes(avant) && apres === 'faite'
}

/** Une même tâche n'est fêtée qu'une fois en quelques secondes (bouton puis relecture). */
export const DELAI_MEME_TACHE_MS = 10_000

export function doitFeter(id: string, maintenant: number, fetees: Map<string, number>): boolean {
  const derniere = fetees.get(id)
  if (derniere !== undefined && maintenant - derniere < DELAI_MEME_TACHE_MS) return false
  fetees.set(id, maintenant)
  return true
}

/**
 * Deux salves, depuis les coins bas gauche et droit, tirées vers le haut et le centre ; la
 * vitesse suit la hauteur de l'écran (relevée de 30 % contre le freinage) : les confettis
 * montent de la moitié de l'écran en moyenne, presque jusqu'en haut pour les plus vifs.
 */
export function creerConfettis(
  largeur: number,
  hauteur: number,
  alea: () => number = Math.random,
): Confetti[] {
  const entre = (min: number, max: number) => min + (max - min) * alea()
  const vitesseMax = 1.3 * Math.sqrt(2 * PESANTEUR * hauteur * 0.9)
  const confettis: Confetti[] = []
  for (const cote of [-1, 1] as const) {
    for (let i = 0; i < NOMBRE; i++) {
      // Angle compté depuis la verticale, penché vers le centre de l'écran.
      const inclinaison = (entre(12, 42) * Math.PI) / 180
      const vitesse = vitesseMax * entre(0.55, 1.05)
      const taille = entre(7, 13)
      confettis.push({
        x: cote === -1 ? entre(-10, 20) : largeur - entre(-10, 20),
        y: hauteur + entre(0, 20),
        vx: -cote * Math.sin(inclinaison) * vitesse,
        vy: -Math.cos(inclinaison) * vitesse,
        angle: entre(0, Math.PI * 2),
        vitesseAngle: entre(-0.2, 0.2),
        bascule: entre(0, Math.PI * 2),
        vitesseBascule: entre(0.08, 0.22),
        largeur: taille,
        hauteur: taille * entre(0.4, 0.7),
        couleur: COULEURS_CONFETTIS[Math.floor(alea() * COULEURS_CONFETTIS.length)] ?? '#ffb800',
        rond: alea() < 0.2,
        ageMs: 0,
      })
    }
  }
  return confettis
}

/** Avance d'un pas de `dtMs` ; faux quand le confetti est sorti par le bas ou a fini sa vie. */
export function avancer(c: Confetti, dtMs: number, hauteur: number): boolean {
  const pas = dtMs / (1000 / 60)
  const freinage = FREINAGE ** pas
  c.vx *= freinage
  c.vy = c.vy * freinage + PESANTEUR * pas
  c.x += (c.vx + Math.sin(c.bascule) * 0.6) * pas
  c.y += c.vy * pas
  c.angle += c.vitesseAngle * pas
  c.bascule += c.vitesseBascule * pas
  c.ageMs += dtMs
  return c.ageMs < VIE_MS && c.y < hauteur + 40
}

// Un seul canvas pour toutes les salves en cours : une tâche close pendant qu'une autre fête
// encore ajoute ses confettis aux siens.
let enVol: Confetti[] = []
let canvas: HTMLCanvasElement | null = null
const fetees = new Map<string, number>()

function moinsDAnimations(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/** Lance les confettis pour la tâche `id` (une fois seulement si on la fête deux fois de suite). */
export function feterTache(id: string): void {
  if (typeof window === 'undefined' || moinsDAnimations()) return
  if (!doitFeter(id, Date.now(), fetees)) return
  enVol.push(...creerConfettis(window.innerWidth, window.innerHeight))
  if (!canvas) demarrer()
}

function demarrer() {
  const c = document.createElement('canvas')
  c.className = 'confettis'
  c.setAttribute('aria-hidden', 'true')
  document.body.appendChild(c)
  canvas = c
  const ctx = c.getContext('2d')
  if (!ctx) {
    arreter()
    return
  }
  let precedent = performance.now()

  const image = (maintenant: number) => {
    // Un onglet passé en arrière-plan ne fait pas sauter les confettis d'un coup.
    const dt = Math.min(maintenant - precedent, 50)
    precedent = maintenant
    const ratio = window.devicePixelRatio || 1
    const largeur = window.innerWidth
    const hauteur = window.innerHeight
    if (c.width !== Math.round(largeur * ratio) || c.height !== Math.round(hauteur * ratio)) {
      c.width = Math.round(largeur * ratio)
      c.height = Math.round(hauteur * ratio)
    }
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, largeur, hauteur)
    enVol = enVol.filter((p) => avancer(p, dt, hauteur))
    for (const p of enVol) dessiner(ctx, p)
    if (enVol.length > 0) requestAnimationFrame(image)
    else arreter()
  }
  requestAnimationFrame(image)
}

function dessiner(ctx: CanvasRenderingContext2D, p: Confetti) {
  const reste = VIE_MS - p.ageMs
  ctx.globalAlpha = reste < EFFACEMENT_MS ? Math.max(reste / EFFACEMENT_MS, 0) : 1
  ctx.fillStyle = p.couleur
  ctx.save()
  ctx.translate(p.x, p.y)
  ctx.rotate(p.angle)
  // Le retournement écrase le confetti sur un axe : il papillonne en tombant.
  ctx.scale(1, Math.cos(p.bascule))
  if (p.rond) {
    ctx.beginPath()
    ctx.arc(0, 0, p.hauteur * 0.7, 0, Math.PI * 2)
    ctx.fill()
  } else {
    ctx.fillRect(-p.largeur / 2, -p.hauteur / 2, p.largeur, p.hauteur)
  }
  ctx.restore()
}

function arreter() {
  canvas?.remove()
  canvas = null
  enVol = []
}
